import React, { useEffect, useRef } from "react";

// Звёздный слой на canvas: дешевле сотен DOM-узлов и не заставляет браузер
// пересчитывать layout при мерцании. При prefers-reduced-motion рисуем один кадр.
const StarField = ({ density = 0.00018, className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let stars = [];
    let frame = 0;
    let width = 0;
    let height = 0;

    const build = () => {
      const parent = canvas.parentElement;
      width = parent?.clientWidth || window.innerWidth;
      height = parent?.clientHeight || window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(220, Math.round(width * height * density));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.1 + 0.35,
        base: Math.random() * 0.45 + 0.25,
        amp: Math.random() * 0.4 + 0.15,
        speed: Math.random() * 0.012 + 0.004,
        phase: Math.random() * Math.PI * 2,
        warm: Math.random() > 0.78,
      }));
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, width, height);
      for (const s of stars) {
        const alpha = reduced
          ? s.base + s.amp * 0.5
          : s.base + s.amp * Math.sin(t * s.speed + s.phase);
        ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
        ctx.fillStyle = s.warm ? "#ffd89a" : "#f6efe3";
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const loop = (time) => {
      draw(time);
      frame = window.requestAnimationFrame(loop);
    };

    build();
    if (reduced) {
      draw(0);
    } else {
      frame = window.requestAnimationFrame(loop);
    }

    const onResize = () => {
      build();
      if (reduced) draw(0);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
};

export default StarField;
